import type { MadinaPaymentConfig, MadinaPaymentMethod, MadinaPaymentType } from 'TYPES/madina';

export type Tier1PaymentSelection = {
  paymentType: MadinaPaymentType;
};

type GatewaySettings = {
  configured: boolean;
  checkoutMode: string;
  publicKey: string | null;
};

const CARD_IDEMPOTENCY_KEY = 'madina_card_idempotency_key';

const readGatewaySettings = (config: MadinaPaymentConfig | undefined): GatewaySettings => {
  const nested = (config?.gateways?.onionpay ?? config?.onionpay) as Record<string, unknown> | undefined;
  const publicKey = nested?.public_key ?? nested?.publicKey;

  return {
    configured: nested?.configured === true,
    checkoutMode: String(nested?.checkout_mode ?? nested?.checkoutMode ?? 'redirect'),
    publicKey: typeof publicKey === 'string' && publicKey ? publicKey : null
  };
};

export const getOnionPayAvailability = (config?: MadinaPaymentConfig) => {
  const onionpay = readGatewaySettings(config);
  const explicitlyOff = config != null && onionpay.configured === false && (config.onionpay != null || config.gateways?.onionpay != null);

  return {
    onionpay,
    cardOnlineAvailable: !explicitlyOff
  };
};

/** Same key for a retry of this card attempt. A new purchase gets a new key. */
export const getOrCreateCardIdempotencyKey = (): string => {
  const existing = sessionStorage.getItem(CARD_IDEMPOTENCY_KEY);
  if (existing && existing.length >= 8 && existing.length <= 64) {
    return existing;
  }
  const key = crypto.randomUUID();
  sessionStorage.setItem(CARD_IDEMPOTENCY_KEY, key);
  return key;
};

export const clearCardIdempotencyKey = () => {
  sessionStorage.removeItem(CARD_IDEMPOTENCY_KEY);
};

export const isPaymentMethodEnabled = (
  methods: MadinaPaymentMethod[],
  type: MadinaPaymentType
): boolean => {
  if (!methods.length) return false;
  const method = methods.find(item => item.type === type);
  return method ? method.enabled !== false : false;
};

export type Tier1PaymentOption = {
  id: string;
  title: string;
  description: string;
  badge?: string;
  selection: Tier1PaymentSelection;
};

export const buildTier1PaymentOptions = (
  methods: MadinaPaymentMethod[],
  _config?: MadinaPaymentConfig
): Tier1PaymentOption[] => {
  const options: Tier1PaymentOption[] = [];

  if (isPaymentMethodEnabled(methods, 'card')) {
    options.push({
      id: 'card-onionpay',
      title: 'Card',
      description: 'OnionPay opens a bank transfer page. Payment is confirmed after the order is marked paid.',
      badge: 'OnionPay',
      selection: { paymentType: 'card' }
    });
  }

  if (isPaymentMethodEnabled(methods, 'transfer')) {
    options.push({
      id: 'bank-transfer',
      title: 'Bank Transfer',
      description: 'Pay by bank transfer.',
      selection: { paymentType: 'transfer' }
    });
  }

  if (isPaymentMethodEnabled(methods, 'wallet')) {
    options.push({
      id: 'wallet',
      title: 'Madina Wallet',
      description: 'Pay instantly from your wallet balance.',
      selection: { paymentType: 'wallet' }
    });
  }

  if (isPaymentMethodEnabled(methods, 'pod')) {
    options.push({
      id: 'pod',
      title: 'Pay on Delivery',
      description: 'Pay when your order arrives. Subject to branch limits.',
      selection: { paymentType: 'pod' }
    });
  }

  return options;
};

/** ERP webhook paths (#1). Browser must never navigate here (GET → MethodNotAllowed). */
export const isOnionPayWebhookUrl = (url: string): boolean => {
  return url.includes('/payment/onionpay/callback') || url.includes('/webhooks/onionpay');
};

/**
 * Frontend return URL (#2) sent as `callback_url` on POST /madina/checkout.
 * Customer lands here after pay; we poll payment-status — UX only. ERP webhook (#1) marks PAID.
 */
export const getMadinaBrowserCallbackUrl = (): string => {
  const explicit = process.env.REACT_APP_PAYMENT_CALLBACK_URL?.trim();
  if (explicit && !isOnionPayWebhookUrl(explicit)) {
    return explicit.replace(/\/$/, '');
  }

  const domain = process.env.REACT_APP_APPLICATION_DOMAIN?.trim().replace(/\/$/, '');
  const origin =
    domain ||
    (typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : 'https://staging.madina.ng');

  return `${origin}/checkout/callback`;
};

export const isLocalCallbackUrl = (callbackUrl: string): boolean => {
  try {
    const hostname = new URL(callbackUrl).hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  } catch {
    return false;
  }
};
